/**
 * ToriWidgetCore - The shared core UI component for TORI Web Agent
 * 
 * This component contains all the shared UI and logic used by:
 * - ToriWebAgent (React library export)
 * - Web Component wrapper (UMD/IIFE bundle)
 * - App.tsx (development demo)
 * 
 * Any changes here will be reflected in all three build targets.
 * 
 * MUST be wrapped by MsalProvider before rendering.
 */

import { useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { McButton } from "@maersk-global/mds-react-wrapper";
import { useToriAuth, useIframeMessaging } from "./hooks";
import type { ToriWidgetCoreProps } from "./types";

// Import shared styles
import "../tori-web-agent.css";
import "@maersk-global/fonts/maeu/fonts.css";
import "@maersk-global/mds-foundations/css/foundations.css";
import "@maersk-global/mds-design-tokens/maersk/light/css/design-tokens-px.css";

/**
 * Props for indicating MSAL configuration status
 * When msalConfigured is false, shows an error message
 */
interface ToriWidgetProps extends ToriWidgetCoreProps {
  /**
   * Whether MSAL is properly configured (CLIENT_ID available)
   */
  msalConfigured?: boolean;
}

/**
 * Core TORI Widget component - must be wrapped by MsalProvider
 * 
 * @param props - Widget configuration
 * @param props.hostContext - Host context containing at least one identifier (appId, platformId, or portfolioId)
 * @param props.iframeSrc - URL of the TORI chat iframe
 * @param props.title - Title displayed in the widget header. Defaults to "TORI".
 * @param props.msalConfigured - Whether MSAL is properly configured (CLIENT_ID available)
 * @param props.children - Optional custom trigger element
 */
export function ToriWidgetCore({ 
  hostContext, 
  iframeSrc,
  title = "TORI",
  prompts,
  msalConfigured = true,
  children,
}: ToriWidgetProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [isEnlarged, setIsEnlarged] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  // Auth state and handlers
  const { isAuthenticated, inProgress, authResult, login, refreshTokens } = useToriAuth();

  // Compute iframe origin for postMessage security
  const iframeOrigin = useMemo(() => {
    try {
      return new URL(iframeSrc).origin;
    } catch {
      return "*";
    }
  }, [iframeSrc]);

  // Set up iframe messaging
  useIframeMessaging(iframeRef, iframeOrigin, authResult, hostContext, refreshTokens, prompts);

  const handleClose = () => {
    setIsVisible(false);
    setIsMounted(false);
    setIsEnlarged(false);
    setIsMinimized(false);
  };

  const handleMinimize = () => {
    if (hasCustomTrigger) {
      setIsMinimized(true);
    } else {
      setIsVisible(false);
    }
  };

  const handleRestore = () => {
    setIsMinimized(false);
  };

  const handleToggleEnlarge = () => {
    setIsEnlarged((prev) => !prev);
  };

  const hasCustomTrigger = !!children;

  const overlayClassName = [
    "tori-modal-overlay",
    hasCustomTrigger ? "tori-modal-overlay--anchored" : "",
    isMinimized ? "tori-modal-overlay--minimized" : "",
    isEnlarged ? "tori-modal-overlay--enlarged" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const modalClassName = [
    "tori-modal-window",
    isEnlarged ? "tori-modal-window--enlarged" : "",
    hasCustomTrigger ? "tori-modal-window--anchored" : "",
    isMinimized ? "tori-modal-window--minimized-anchored" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <>
      {/* Trigger – custom child element or default floating action button */}
      {children ? (
        <div
          className="tori-custom-trigger"
          role="button"
          tabIndex={0}
          onClick={() => { setIsMounted(true); setIsVisible(true); }}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setIsMounted(true); setIsVisible(true); } }}
        >
          {children}
        </div>
      ) : (
        <button
          className="tori-floating-button"
          aria-label="Open chat window"
          onClick={() => { setIsMounted(true); setIsVisible(true); }}
        >
          <img 
            src="https://tori.maersk.io/tori-avatar.svg" 
            style={{ height: 58, width: 58 }} 
            alt="TORI" 
          />
        </button>
      )}

      {/* Modal – rendered via portal to avoid CSS containment/layout issues in host apps */}
      {isMounted && createPortal(
        <div 
          className={overlayClassName} 
          style={{ display: isVisible ? undefined : 'none' }}
        >
          <div className={modalClassName} onClick={(e) => e.stopPropagation()}>
            <div
              className="tori-modal-header"
              onClick={isMinimized && hasCustomTrigger ? handleRestore : undefined}
              style={isMinimized && hasCustomTrigger ? { cursor: 'pointer' } : undefined}
            >
              <h3>{title}</h3>
              <div className="tori-header-actions">
                <button
                  type="button"
                  className="tori-icon-button tori-minimize-button"
                  aria-label={isMinimized ? "Restore window" : "Minimize window"}
                  onClick={isMinimized && hasCustomTrigger ? handleRestore : handleMinimize}
                >
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path
                      d="M5 12h14"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                </button>

                <button
                  type="button"
                  className="tori-icon-button tori-enlarge-button"
                  aria-label={isEnlarged ? "Restore default size" : "Enlarge to tablet size"}
                  onClick={handleToggleEnlarge}
                >
                  {
                    isEnlarged ? <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="2"
                  >
                    <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9 9V4.5M9 9H4.5M9 9 3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5 5.25 5.25" />
                  </svg> : <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="2"
                  >
                    <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" />
                  </svg>
                  }
                </button>

                <button
                  type="button"
                  className="tori-icon-button tori-close-button"
                  aria-label="Close window"
                  onClick={handleClose}
                >
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path
                      d="M18 6L6 18M6 6l12 12"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                </button>
              </div>
            </div>
            <div className="tori-iframe-container">
              {!isAuthenticated ? (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100%",
                  }}
                >
                  {!msalConfigured && (
                    <p style={{ color: "#a00", margin: 0, textAlign: "center" }}>
                      Missing VITE_AZURE_CLIENT_ID. Set it in your environment.
                    </p>
                  )}

                  <McButton
                    label={inProgress === "login" ? "Signing in…" : "Login with Maersk ID"}
                    appearance="primary"
                    type="submit"
                    disabled={!msalConfigured || inProgress === "login"}
                    onClick={login}
                  />
                </div>
              ) : (
                <iframe
                  ref={iframeRef}
                  src={iframeSrc}
                  title="TORI"
                  sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox"
                />
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

export default ToriWidgetCore;
